import type { Statute } from './types';
import { adaptStatute as adaptV2CivilsSuits } from './adapters/v2CivilsSuitsAdapter';
import { adaptStatute as adaptV2FamilySuits } from './adapters/v2FamilySuitsAdapter';
import { adaptStatute as adaptV2Constitution } from './adapters/v2ConstitutionAdapter';
import { adaptStatute as adaptLegacyCriminal } from './adapters/legacyCriminalAdapter';

type AnyRecord = Record<string, unknown>;

const isObject = (v: unknown): v is AnyRecord =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

function structureHas(json: AnyRecord, predicate: (node: AnyRecord) => boolean): boolean {
  const stack: unknown[] = Array.isArray(json.structure) ? [...(json.structure as unknown[])] : [];
  let safety = 5000;
  while (stack.length && safety-- > 0) {
    const node = stack.pop();
    if (!isObject(node)) continue;
    if (predicate(node)) return true;
    if (Array.isArray(node.children)) stack.push(...(node.children as unknown[]));
  }
  return false;
}

function isLegacyCriminal(json: AnyRecord): boolean {
  if (json.schemaVersion) return false;
  if (!('parts' in json) && !('chapters' in json)) return false;
  if (Array.isArray(json.parts)) return true;
  if (Array.isArray(json.chapters)) {
    // Heuristic: legacy chapters carry sections with `section`/`title`/`content`.
    const ch = json.chapters as unknown[];
    for (const c of ch) {
      if (!isObject(c)) continue;
      const sections = Array.isArray(c.sections) ? c.sections : [];
      for (const s of sections) {
        if (isObject(s) && (typeof s.section === 'string' || typeof s.section === 'number')) {
          return true;
        }
      }
    }
  }
  return false;
}

function isV2(json: AnyRecord): boolean {
  return json.schemaVersion === 'v2' && Array.isArray(json.structure);
}

function isV2Constitution(json: AnyRecord, hint?: string): boolean {
  if (!isV2(json)) return false;
  const hintLower = hint ? hint.toLowerCase() : '';
  if (hintLower.includes('write-petition') || hintLower.includes('constitution')) return true;
  // The Constitution carries `articleNo` on its leaves and is article-centric.
  return structureHas(json, (n) => typeof n.articleNo === 'string' || (n.type === 'article' && typeof n.id === 'string' && /^art-/i.test(n.id)));
}

function isV2FamilySuits(json: AnyRecord, hint?: string): boolean {
  if (!isV2(json)) return false;
  const hintLower = hint ? hint.toLowerCase() : '';
  if (hintLower.includes('family-suits') || hintLower.includes('family')) return true;
  // Family-suits files carry `sectionNo` or use `scheduleItem` leaves. They
  // also typically have sections at the top level (no preliminary/part wrappers).
  if (structureHas(json, (n) => n.type === 'scheduleItem')) return true;
  if (structureHas(json, (n) => typeof n.sectionNo === 'string')) {
    // sectionNo alone is not exclusive to family-suits, but combined with a
    // flat top-level (sections at the root, no preliminary/part wrapper) it's
    // a strong signal.
    const structure = Array.isArray(json.structure) ? (json.structure as unknown[]) : [];
    const hasPartOrPreliminary = structure.some(
      (n) => isObject(n) && (n.type === 'part' || n.type === 'preliminary'),
    );
    if (!hasPartOrPreliminary) return true;
  }
  return false;
}

function isV2CivilsSuits(json: AnyRecord): boolean {
  return isV2(json);
}

/**
 * Dispatch to the right adapter for the given raw JSON.
 *
 * `hint` may be a category string (e.g. the Firestore `statute.category`) or a
 * filename / path. It's used to disambiguate when schema heuristics alone are
 * insufficient (e.g. distinguishing constitution from civils-suits when a v2
 * file is article-centric).
 *
 * Detection order (most specific first):
 *   1. legacy criminal (no schemaVersion)
 *   2. v2 constitution (article-centric / hint)
 *   3. v2 family-suits (scheduleItem / sectionNo with flat top-level / hint)
 *   4. v2 civils-suits (default for any other v2)
 *
 * Throws when nothing matches so the caller can fall back to the legacy
 * text parser.
 */
export function adaptStatuteAny(rawJson: unknown, hint?: string): Statute {
  if (!isObject(rawJson)) {
    throw new Error('adaptStatuteAny: rawJson is not an object');
  }

  if (isLegacyCriminal(rawJson)) return adaptLegacyCriminal(rawJson, hint);
  if (isV2Constitution(rawJson, hint)) return adaptV2Constitution(rawJson, hint);
  if (isV2FamilySuits(rawJson, hint)) return adaptV2FamilySuits(rawJson, hint);
  if (isV2CivilsSuits(rawJson)) return adaptV2CivilsSuits(rawJson, hint);

  throw new Error(
    `adaptStatuteAny: unrecognized statute format${hint ? ` (hint=${hint})` : ''}`,
  );
}
